/**
 * Founder Control Center · SubTabs L2.
 *
 * Barra de pestañas internas que cada sección (Dashboard · Intelligence · Growth ·
 * Operations · AI Agents · Security) usa para alternar entre sus sub-módulos.
 *
 * Patrón:
 *   <SubTabs items={[{ key, label, icon? }]} value={...} onChange={...} testid="..." />
 *
 * Sincroniza con `?sub=KEY` en la URL para deep-link.
 */
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export function useSubTab(defaultKey, paramKey = "sub") {
  const [params, setParams] = useSearchParams();
  const current = params.get(paramKey) || defaultKey;
  const setCurrent = (next) => {
    const np = new URLSearchParams(params);
    if (!next || next === defaultKey) np.delete(paramKey);
    else np.set(paramKey, next);
    setParams(np, { replace: true });
  };
  // Si la URL trae uno desconocido, no rompe (el render decide).
  useEffect(() => { /* no-op: la sincronización es de lectura */ }, []);
  return [current, setCurrent];
}

export default function SubTabs({ items, value, onChange, testid = "subtabs" }) {
  return (
    <div className="border-b border-[var(--ag-border)] -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8" data-testid={testid}>
      <div className="flex items-center gap-1 overflow-x-auto whitespace-nowrap">
        {items.map((item, idx) => {
          if (item.type === "separator") {
            return (
              <span
                key={`sep-${idx}`}
                aria-hidden="true"
                className="mx-2 select-none text-sm text-[var(--ag-text-dim)] opacity-40"
                data-testid={`${testid}-separator-${idx}`}
              >
                ·
              </span>
            );
          }
          const { key, label, icon: Icon, disabled } = item;
          const active = value === key;
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onChange(key)}
              className={`px-3 py-2.5 text-xs sm:text-sm font-semibold border-b-2 -mb-px transition-colors ${
                active
                  ? "border-[var(--ag-gold)] text-[var(--ag-text)]"
                  : "border-transparent text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
              } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid={`${testid}-${key}`}
            >
              {Icon && <Icon size={13} className="inline mr-1.5" weight="duotone" />}
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
